// ─────────────────────────────────────────────────────────────────────────────
// Notificação do sistema operacional.
//
// O Orion avisa por toast e, quando o assunto é sério, por modal. Os dois têm
// o mesmo limite: só existem DENTRO da janela. Se o operador está no almoço com
// o navegador minimizado, ninguém vê nem um nem outro.
//
// A Notification API sai da página e entra na bandeja do Windows. É o único
// canal do Orion que alcança quem não está olhando para ele.
//
// ─────────────────────────────────────────────────────────────────────────────
// A PERMISSÃO PRECISA DE UM CLIQUE
//
// `Notification.requestPermission()` chamado sozinho, num temporizador, é
// ignorado pelo Chrome: o navegador exige que o pedido nasça de um gesto do
// operador. Por isso `garantirPermissao` é chamada quando ele MARCA a pausa —
// ali houve clique, e é o momento em que a permissão passa a fazer sentido.
//
// Pedir de novo depois de um "não" também não adianta: a resposta fica gravada
// e a chamada volta na hora com 'denied'. Então quem foi negado simplesmente
// segue com toast e modal, sem insistir a cada pausa.
// ─────────────────────────────────────────────────────────────────────────────

const temApi = typeof window !== 'undefined' && 'Notification' in window;

/** True quando este ambiente sabe notificar (navegador moderno e Electron). */
export function suportaNotificacao() {
  return temApi;
}

/** 'granted' | 'denied' | 'default' | 'indisponivel'. */
export function permissaoAtual() {
  if (!temApi) return 'indisponivel';
  return Notification.permission;
}

/**
 * Garante a permissão, pedindo-a se ainda não houve resposta.
 *
 * Chame SEMPRE de dentro de um manipulador de clique. Devolve `true` quando
 * pode notificar. Nunca lança: notificação é acessório, e uma falha aqui não
 * pode derrubar a ação que o operador acabou de fazer.
 */
export async function garantirPermissao() {
  if (!temApi) return false;
  if (Notification.permission === 'granted') return true;
  if (Notification.permission === 'denied') return false;

  try {
    return (await Notification.requestPermission()) === 'granted';
  } catch (e) {
    return false;
  }
}

/**
 * Mostra a notificação. Devolve `true` se ela saiu.
 *
 * `tag` faz a notificação nova SUBSTITUIR a anterior de mesma etiqueta, em vez
 * de empilhar. É o que se quer num lembrete que se repete a cada 15 minutos:
 * quem voltou do almoço às 14h não pode encontrar seis avisos enfileirados.
 *
 * `insistir` mantém o balão na tela até o operador mexer nele, em vez de sumir
 * sozinho em poucos segundos — para quem está de costas, sumir é não avisar.
 */
export function notificar(titulo, corpo, { tag = 'orion', insistir = false } = {}) {
  if (!temApi || Notification.permission !== 'granted') return false;

  try {
    const n = new Notification(titulo, {
      body: corpo,
      tag,
      renotify: true,
      requireInteraction: insistir,
      silent: false,
    });

    // Clicar no balão traz o Orion para a frente — é o que o operador espera
    // que aconteça, e sem isso ele teria de procurar a janela na barra.
    n.onclick = () => {
      try { window.focus(); } catch (e) { /* a janela pode ter sido fechada */ }
      try { n.close(); } catch (e) { /* já fechada */ }
    };

    return true;
  } catch (e) {
    // Alguns ambientes recusam o construtor mesmo com permissão concedida
    // (Android, service worker exigido). Segue a vida: o toast e o modal ainda
    // estão de pé.
    console.warn('[Orion] Notificação do sistema:', e.message);
    return false;
  }
}
